import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, useClerk, SignIn } from '@clerk/clerk-react';
import { useSubscription } from '../../contexts/SubscriptionContext.js';
import {
  Menu as MenuIcon,
  X,
  User,
  BookOpen,
  Library,
  LogOut,
  LogIn,
  ShieldCheck,
} from 'lucide-react';

import { Button } from '../ui/button.jsx';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar.jsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.jsx';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog.jsx';

const navLinks = [
  { name: 'Characters', path: '/characters', icon: User },
  { name: 'Story Wizard', path: '/wizard', icon: BookOpen },
  { name: 'My Library', path: '/library', icon: Library },
];

function Header() {
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSignInOpen, setIsSignInOpen] = useState(false);
  const navigate = useNavigate();
  const { isAdmin: serverIsAdmin } = useSubscription();

  const userDisplayName = user?.fullName || user?.primaryEmailAddress?.emailAddress || 'User';
  const userAvatarUrl = user?.imageUrl;
  const userInitial = userDisplayName.charAt(0).toUpperCase();
  const isAdmin = serverIsAdmin || user?.publicMetadata?.isAdmin === true;

  const handleLogout = async () => {
    setIsMobileMenuOpen(false);
    await signOut();
    navigate('/');
  };

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      className="fixed top-0 left-0 right-0 z-50 bg-background shadow-sm"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/favicon.ico" alt="Make It Mindful logo" className="h-8 w-8" />
          <span className="text-xl font-bold text-primary hover:text-primary/80 transition-colors">
            Make It Mindful
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden sm:flex sm:items-center sm:gap-6">
          <div className="flex items-center gap-6">
            {navLinks.map(({ name, path, icon: Icon }) => (
              <Link
                key={name}
                to={path}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
              >
                <Icon className="h-4 w-4" />
                {name}
              </Link>
            ))}
          </div>

          {isSignedIn ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label="Open user menu"
                >
                  <Avatar>
                    {userAvatarUrl && <AvatarImage src={userAvatarUrl} alt={userDisplayName} />}
                    <AvatarFallback>{userInitial}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{userDisplayName}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/characters" className="flex items-center gap-2 cursor-pointer">
                    <User className="h-4 w-4" />
                    My Characters
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/library" className="flex items-center gap-2 cursor-pointer">
                    <Library className="h-4 w-4" />
                    My Library
                  </Link>
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                      <ShieldCheck className="h-4 w-4" />
                      Admin Panel
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleLogout} className="flex items-center gap-2 cursor-pointer">
                  <LogOut className="h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={() => setIsSignInOpen(true)} aria-label="Sign in to your account">
              Sign In
            </Button>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="sm:hidden inline-flex items-center justify-center rounded-md p-2 text-foreground/70 hover:text-primary hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setIsMobileMenuOpen((v) => !v)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div
          id="mobile-menu"
          className="sm:hidden border-t border-border bg-background shadow-md"
          role="menu"
        >
          <div className="flex flex-col py-2">
            {navLinks.map(({ name, path, icon: Icon }) => (
              <Link
                key={name}
                to={path}
                className="flex items-center gap-2 px-4 py-3 text-base font-medium text-foreground/80 hover:text-primary hover:bg-accent/30"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Icon className="h-5 w-5" />
                {name}
              </Link>
            ))}
            <div className="border-t border-border mt-1 pt-1">
              {isSignedIn ? (
                <>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-2 px-4 py-3 text-base font-medium text-foreground/80 hover:text-primary hover:bg-accent/30"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <ShieldCheck className="h-5 w-5" />
                      Admin Panel
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-3 text-left text-base font-medium text-foreground/80 hover:text-primary hover:bg-accent/30"
                  >
                    <LogOut className="h-5 w-5" />
                    Logout
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => { setIsSignInOpen(true); setIsMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-4 py-3 text-left text-base font-medium text-foreground/80 hover:text-primary hover:bg-accent/30"
                >
                  <LogIn className="h-5 w-5" />
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sign-in dialog (mounts Clerk's hosted SignIn component) */}
      <Dialog open={isSignInOpen} onOpenChange={setIsSignInOpen}>
        <DialogContent className="sm:max-w-md p-0 bg-transparent border-none shadow-none">
          <DialogTitle className="sr-only">Sign in</DialogTitle>
          <SignIn
            redirectUrl="/characters"
            appearance={{
              elements: {
                rootBox: { width: '100%' },
                card: { boxShadow: 'none', border: 'none' },
              },
            }}
          />
        </DialogContent>
      </Dialog>
    </nav>
  );
}

export default Header;
